import { Link } from 'react-router-dom'
import { Trophy } from 'lucide-react'
import { formatDashboardCurrency } from '@/utils/crmValuesDisplay'
import { formatDate } from '@/utils/format-date'
import { dealsPagePathForDeal } from '@/utils/dealsPageNavigation'
import type { PipelineClosedDealRow } from '@/components/Dashboard/pipelineTab.utils'

type Props = {
  deals: PipelineClosedDealRow[]
  valuesVisible?: boolean
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

export function PipelineClosedDealsTable({ deals, valuesVisible = false }: Props) {
  return (
    <div className='rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] shadow-sm'>
      <div className='flex flex-wrap items-end justify-between gap-3 border-b border-[var(--border)] px-5 py-4'>
        <div>
          <div className='flex items-center gap-2'>
            <Trophy size={16} className='text-emerald-500' aria-hidden />
            <h3 className='text-base font-semibold text-text-primary'>Últimos fechamentos</h3>
          </div>
          <p className='mt-1 text-sm text-text-muted'>
            Deals ganhos com data de fechamento no período
          </p>
        </div>
        <Link
          to='/deals'
          className='rounded-full border border-[var(--border)] px-3 py-1 text-xs font-semibold text-text-secondary hover:bg-[var(--bg-dark)]/20'
        >
          Ver negócios
        </Link>
      </div>

      {!deals.length ? (
        <p className='px-5 py-10 text-center text-sm text-text-muted'>
          Nenhum fechamento registrado no período selecionado.
        </p>
      ) : (
        <div className='overflow-x-auto'>
          <table className='min-w-full text-left text-sm'>
            <thead>
              <tr className='border-b border-[var(--border)] text-xs uppercase tracking-wide text-text-muted'>
                <th className='px-5 py-3 font-semibold'>Negócio</th>
                <th className='px-3 py-3 font-semibold'>Valor</th>
                <th className='px-3 py-3 font-semibold'>Empresa</th>
                <th className='px-3 py-3 font-semibold'>Closer</th>
                <th className='px-5 py-3 font-semibold'>Fechamento</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((deal) => (
                <tr
                  key={deal.id}
                  className='border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-dark)]/10'
                >
                  <td className='px-5 py-3.5'>
                    <Link
                      to={dealsPagePathForDeal(deal.id)}
                      className='flex min-w-[200px] items-center gap-3 font-medium text-text-primary hover:text-accent'
                    >
                      <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-700'>
                        {initials(deal.nome)}
                      </span>
                      <span className='truncate'>{deal.nome}</span>
                    </Link>
                  </td>
                  <td className='px-3 py-3.5 font-semibold tabular-nums text-text-primary'>
                    {formatDashboardCurrency(deal.valor, valuesVisible)}
                  </td>
                  <td className='px-3 py-3.5 text-text-secondary'>{deal.empresa}</td>
                  <td className='px-3 py-3.5 text-text-secondary'>{deal.owner}</td>
                  <td className='px-5 py-3.5 text-text-muted'>
                    {deal.dataFechamento ? formatDate(deal.dataFechamento) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
